import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, CheckCircle2 } from "lucide-react";
import { api } from "../lib/api";

const errText = (e) => {
  const d = e.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x.msg?.replace("Value error, ", "")).join(" ");
  return "Something went wrong. Please try again.";
};
const LINKS = [["website", "Official website"], ["volunteer_url", "Volunteer page"], ["donation_url", "Donation page"], ["events_url", "Events page"]];
const Field = ({ label, k, v, set, type = "text", required, testId, textarea }) => (
  <label className="block text-sm">
    <span className="font-medium text-slate-800">{label}{required && " *"}</span>
    {textarea ? <textarea value={v[k] || ""} onChange={(e) => set(k, e.target.value)} rows={3} data-testid={testId} className="mt-1 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-slate-900" />
      : <input type={type} value={v[k] || ""} onChange={(e) => set(k, e.target.value)} required={required} data-testid={testId} className="mt-1 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-slate-900" />}
  </label>
);

export default function ClaimOrganization() {
  const { slug } = useParams();
  const [org, setOrg] = useState(null);
  const [v, setV] = useState({});
  const [state, setState] = useState({ busy: false, error: "", done: null });
  const set = (k, x) => setV((p) => ({ ...p, [k]: x }));
  useEffect(() => {
    api.get(`/organizations/${slug}`).then((r) => {
      const o = r.data.organization;
      setOrg(o);
      setV({ website: o.website, volunteer_url: o.volunteer_url, donation_url: o.donation_url, events_url: o.events_url, email: o.email, phone: o.phone, description: o.description });
    }).catch(() => setOrg(false));
  }, [slug]);
  const submit = async (e) => {
    e.preventDefault();
    setState({ busy: true, error: "", done: null });
    const body = Object.fromEntries(Object.entries({ ...v, org_slug: slug }).filter(([k, x]) => x || (x === "" && org[k])).map(([k, x]) => [k, x ?? ""]));
    try { const r = await api.post("/claims", body); setState({ busy: false, error: "", done: r.data }); }
    catch (err) { setState({ busy: false, error: errText(err), done: null }); }
  };
  if (org === false) return <p className="py-24 text-center" data-testid="claim-missing">Organization not found.</p>;
  if (!org) return <div className="mx-auto max-w-3xl px-5 py-16"><div className="h-96 animate-pulse rounded-3xl bg-white" /></div>;
  return (
    <div className="mx-auto max-w-3xl px-5 py-10 sm:px-8" data-testid="claim-page">
      <Link to={`/organizations/${slug}`} className="inline-flex items-center gap-1 text-sm text-sky-800"><ArrowLeft size={15} />Back to {org.name}</Link>
      <h1 className="mt-4 text-3xl font-bold tracking-tight text-slate-900">Claim or update {org.name}</h1>
      <p className="mt-2 text-sm text-slate-700">Work for this organization? Correct your links so volunteers and donors reach you directly — clear a field to remove a wrong link. Every change is reviewed by the RippleImpact team before it goes live.</p>
      {state.done ? (
        <div className="mt-8 rounded-3xl border border-emerald-200 bg-emerald-50 p-6" data-testid="claim-success">
          <p className="flex items-center gap-2 font-semibold text-emerald-900"><CheckCircle2 size={18} />Thanks — your claim was submitted for review.</p>
          <p className="mt-2 text-sm text-emerald-900">{state.done.changes.length ? `Proposed changes: ${state.done.changes.join(", ").replaceAll("_", " ")}.` : "No link changes were proposed; we'll still review your claim."}</p>
        </div>
      ) : (
        <form onSubmit={submit} className="mt-8 space-y-6 rounded-3xl border border-slate-200 bg-white p-6" data-testid="claim-form">
          <fieldset className="grid gap-4 sm:grid-cols-2"><legend className="mb-2 font-semibold text-slate-900">About you</legend>
            <Field label="Your name" k="contact_name" v={v} set={set} required testId="claim-contact-name" />
            <Field label="Your role" k="contact_role" v={v} set={set} required testId="claim-contact-role" />
            <div className="sm:col-span-2"><Field label="Work email (ideally on your organization's domain)" k="contact_email" v={v} set={set} type="email" required testId="claim-contact-email" /></div>
          </fieldset>
          <fieldset className="grid gap-4"><legend className="mb-2 font-semibold text-slate-900">Your organization's links</legend>
            {LINKS.map(([k, l]) => <Field key={k} label={l} k={k} v={v} set={set} type="url" testId={`claim-${k}`} />)}
            <div className="grid gap-4 sm:grid-cols-2"><Field label="Public email" k="email" v={v} set={set} type="email" testId="claim-email" /><Field label="Phone" k="phone" v={v} set={set} testId="claim-phone" /></div>
            <Field label="Mission / description" k="description" v={v} set={set} textarea testId="claim-description" />
            <Field label="Anything else we should know?" k="message" v={v} set={set} textarea testId="claim-message" />
          </fieldset>
          {state.error && <p className="rounded-xl bg-rose-50 px-3 py-2 text-sm text-rose-700" data-testid="claim-error">{state.error}</p>}
          <button disabled={state.busy} className="btn-green rounded-full px-6 py-3 text-sm font-semibold disabled:opacity-60" data-testid="claim-submit">{state.busy ? "Submitting…" : "Submit for review"}</button>
        </form>
      )}
    </div>
  );
}
